/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { convertDDMMYYYtoYYYYMMDD } from '../../../utils/dates';
import { ExcelForecastCreatePayload, NewForecastDataSource } from '../../dashboard/types';
import { ForecastDatasourceProps } from '../components/form-elements/forecast-datasource';
import { ForecastDurationProps } from '../components/form-elements/forecast-duration';
import { ForecastNameProps } from '../components/form-elements/forecast-name';
import { ForecastPreviousActualsProps } from '../components/form-elements/forecast-previous-actuals';
import { ForecastSourceForecastProps } from '../components/form-elements/forecast-source-forecast';
import { getCreateForecastRequestData, getForecastWindowPayload } from '../get-create-forecast-request-data';
import {
  FIXED_FORECAST_CHOICES,
  ForecastFormElementIds,
  ForecastKindOption,
  ForecastTypeOption,
  ROLLING_FORECAST_CHOICES
} from '../types';
import { useForecastStartDateField } from './useForecastStartDateField';

type ForcastFormInitialValues = Partial<{
  datasource: NewForecastDataSource | '';
  durationFixed: string;
  durationRolling: string;
  from: string;
  name: string;
  previousActuals: boolean;
  sourceForecast: string;
  type: ForecastTypeOption;
}>;

export const useCreateForecastForm = (
  kind: ForecastKindOption,
  initialValues: ForcastFormInitialValues = {},
  organisationCreated?: string
) => {
  const [datasource, setDataSource] = useState<NewForecastDataSource | ''>(initialValues.datasource ?? '');
  const [durationFixed, setDurationFixed] = useState(initialValues.durationFixed ?? '');
  const [durationRolling, setDurationRolling] = useState(initialValues.durationRolling ?? '');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [name, setName] = useState(initialValues.name ?? '');
  const [previousActuals, setPreviousActuals] = useState(initialValues.previousActuals ?? false);
  const [sourceForecast, setSourceForecast] = useState(initialValues.sourceForecast ?? '');
  const [type, setType] = useState<ForecastTypeOption | undefined>(initialValues.type);

  const intl = useIntl();
  const hasBeenValidated = useRef(false);
  const { from, getStartDate, maxDate, minDate, onChangeFrom, setFrom, validateStartDate } =
    useForecastStartDateField({
      initialFrom: initialValues.from,
      organisationCreated,
      setErrors
    });

  const getValidationErrors = useCallback(() => {
    const newErrors: WizardFormErrors = {};

    // Name
    if (!name.trim().length) {
      if (kind === 'budget') {
        newErrors.name = {
          id: ForecastFormElementIds.Name,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.budget_name_is_required',
            defaultMessage: 'Budget name is required'
          })
        };
      } else {
        newErrors.name = {
          id: ForecastFormElementIds.Name,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.forecast_name_is_required',
            defaultMessage: 'Forecast name is required'
          })
        };
      }
    }

    const startDateError = validateStartDate();
    if (startDateError) {
      newErrors.from = startDateError;
    }

    // Datasource
    if (!datasource) {
      newErrors.datasource = {
        id: ForecastFormElementIds.Datasource,
        message: intl.formatMessage({
          id: 'predict.create_forecast.validation.please_select_an_option',
          defaultMessage: 'Please select an option'
        })
      };
    } else if (datasource === NewForecastDataSource.FromDuplicate) {
      if (!sourceForecast.trim().length) {
        if (kind === 'budget') {
          newErrors.sourceForecast = {
            id: ForecastFormElementIds.SourceForecast,
            message: intl.formatMessage({
              id: 'predict.create_forecast.validation.you_must_select_a_budget_to_copy',
              defaultMessage: 'You must select a budget to copy'
            })
          };
        } else {
          newErrors.sourceForecast = {
            id: ForecastFormElementIds.SourceForecast,
            message: intl.formatMessage({
              id: 'predict.create_forecast.validation.you_must_select_a_forecast_to_copy',
              defaultMessage: 'You must select a forecast to copy'
            })
          };
        }
      }
    }

    // P&L budgets are always fixed for one year, so the redesigned budget form
    // intentionally does not expose forecast type or duration fields.
    if (kind === 'scenario') {
      if (type === 'fixed' && !FIXED_FORECAST_CHOICES.includes(durationFixed)) {
        newErrors.duration = {
          id: ForecastFormElementIds.Duration,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.please_select_an_option',
            defaultMessage: 'Please select an option'
          })
        };
      } else if (type === 'rolling' && !ROLLING_FORECAST_CHOICES.includes(durationRolling)) {
        newErrors.duration = {
          id: ForecastFormElementIds.Duration,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.please_select_an_option',
            defaultMessage: 'Please select an option'
          })
        };
      }

      if (type !== ForecastTypeOption.Fixed && type !== ForecastTypeOption.Rolling) {
        newErrors.type = {
          id: ForecastFormElementIds.Type,
          message: intl.formatMessage({
            id: 'predict.create_forecast.validation.please_select_an_option',
            defaultMessage: 'Please select an option'
          })
        };
      }
    }

    return newErrors;
  }, [datasource, durationFixed, durationRolling, intl, kind, name, sourceForecast, type, validateStartDate]);

  const invalidFields = Object.keys(getValidationErrors());
  const isConfigurationValid = invalidFields.length === 0;
  // Copy-source requirements do not affect the template's forecast configuration.
  const isTemplateConfigurationValid = invalidFields.every(field => field === 'datasource' || field === 'sourceForecast');

  const validate = useCallback(() => {
    const newErrors = getValidationErrors();
    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [getValidationErrors]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  const onChangeDatasource: ForecastDatasourceProps['onChange'] = useCallback((e: any) => {
    setDataSource(e.target.value);
  }, []);
  const onChangeDurationFixed: ForecastDurationProps['onChange'] = useCallback((e: any) => {
    setDurationFixed(e.target.value);
  }, []);
  const onChangeDurationRolling: ForecastDurationProps['onChange'] = useCallback((e: any) => {
    setDurationRolling(e.target.value);
  }, []);
  const onChangeName: ForecastNameProps['onChange'] = useCallback((e: any) => {
    setName(e.target.value);
  }, []);
  const onChangePreviousActuals: ForecastPreviousActualsProps['onChange'] = useCallback((e: any) => {
    setPreviousActuals(e.target.checked);
  }, []);
  const onChangeSourceForecast: ForecastSourceForecastProps['onChange'] = useCallback((e: any) => {
    setSourceForecast(e.target.value);
  }, []);
  const onChangeType = useCallback((e: any) => {
    setType(e.target.value);
    setFrom('');
  }, [setFrom]);

  const getRequestData = useCallback(() => {
    return getCreateForecastRequestData({
      datasource: datasource as NewForecastDataSource,
      durationFixed,
      durationRolling,
      from: getStartDate(),
      kind,
      name,
      previousActuals,
      sourceForecast,
      type
    });
  }, [datasource, durationFixed, durationRolling, getStartDate, kind, name, previousActuals, sourceForecast, type]);

  const getExcelRequestData = useCallback(
    (kind: 'budget' | 'scenario'): ExcelForecastCreatePayload => {
      const forecastFromDate = convertDDMMYYYtoYYYYMMDD(getStartDate())!;
      const windowPayload = getForecastWindowPayload({
        durationFixed,
        durationRolling,
        kind,
        previousActuals,
        type,
        windowFrom: forecastFromDate
      });
      return {
        forecast_name: name,
        forecast_kind: kind,
        forecast_id: null,
        include_unpaid_invoices: kind === 'scenario' && type === 'rolling',
        categories_with_ai_enabled:
          datasource === NewForecastDataSource.WithAutoPredictions
            ? ['income', 'cost_of_sales', 'expenses', 'other_income', 'other_expenses']
            : null,
        ...windowPayload
      };
    },
    [datasource, durationFixed, durationRolling, getStartDate, name, previousActuals, type]
  );

  return {
    datasource,
    durationFixed,
    durationRolling,
    errors,
    from,
    name,
    previousActuals,
    sourceForecast,
    maxDate,
    minDate,
    type,
    getExcelRequestData,
    getRequestData,
    isConfigurationValid,
    isTemplateConfigurationValid,
    onChangeDatasource,
    onChangeDurationFixed,
    onChangeDurationRolling,
    onChangeFrom,
    onChangeName,
    onChangePreviousActuals,
    onChangeSourceForecast,
    onChangeType,
    revalidate,
    setFrom,
    validate
  };
};
